import classNames from 'classnames';
import styles from './MediaStory.module.scss';
import { ConsentPlaceholder, Heading, Paragraph, Player, Icon } from 'ui/components';
import ChevronIcon from 'ui/icons/icon-chevron.svg';
import { useState, useEffect } from 'react';
import { LinkButton } from 'ui/components/1-atoms';
import { Container } from 'ui/components/4-habitats';
import { GetImageUrl } from 'helpers/getImageUrl';
import { useConsent, YOUTUBE_REQUIRED_GROUPS } from 'application/adapters/context/ConsentContext';
import { isYouTube } from 'helpers/youtubeEmbed';

export interface MediaStoryProps {
	heading: string;
	subHeading: string;
	lead: string;
	videoUrl: string;
	videoThumbnail?: Umbraco.Image;
	buttonText: string;
	link?: Umbraco.Link;
	consentHeading: string;
	consentText: string;
}

export const MediaStory: React.FC<MediaStoryProps> = ({
	heading = 'Story about polishing',
	subHeading,
	lead,
	videoUrl,
	videoThumbnail,
	buttonText,
	link,
	consentHeading,
	consentText,
}) => {
	const [isActive, setIsActive] = useState(false);
	const hasYouTubeConsent = useConsent(YOUTUBE_REQUIRED_GROUPS);
	const toggleVisibility = () => {
		setIsActive(!isActive);
	};

	const url = videoThumbnail?.url;
	const thumbnailImage = { width: 1280, height: 720, aspectRatio: 1.778 };
	const [videoPoster, setVideoPoster] = useState(null);

	useEffect(() => {
		const revealThumbnail = () => {
			const thumbnailUrl = GetImageUrl({
				url,
				width: thumbnailImage.width,
			});
			const thumbnailUrlWebp = GetImageUrl({
				url,
				width: thumbnailImage.width,
				format: 'webp',
			});

			if (!url?.includes('.webp')) {
				setVideoPoster(thumbnailUrl);
			} else {
				setVideoPoster(thumbnailUrlWebp);
			}
		};

		if (url) {
			revealThumbnail();
		}
	}, [thumbnailImage.height, thumbnailImage.width, url]);

	return (
		<section className={styles.MediaStory}>
			<Container width="Standard">
				{/* HEADING */}
				{heading && (
					<div
						className={classNames(styles.MediaStory_heading, {
							[styles.isClosed]: isActive,
						})}
					>
						<Heading headingLevel="h2" style="md">
							{heading}
						</Heading>
						<div onClick={toggleVisibility}>
							<Icon size="xl" className={styles.MediaStory_buttonIcon}>
								<ChevronIcon />
							</Icon>
						</div>
					</div>
				)}

				<div
					className={classNames(styles.MediaStory_grid, {
						[styles.isClosed]: isActive,
					})}
				>
					<div className={styles.MediaStory_video}>
						{/* MEDIA PLAYER - not mounted without consent, it preloads noembed, i.ytimg and iframe_api */}
						{isYouTube(videoUrl) && !hasYouTubeConsent ? (
							<ConsentPlaceholder heading={consentHeading} text={consentText} />
						) : (
							<Player url={videoUrl} poster={videoPoster} />
						)}
					</div>

					<div className={styles.MediaStory_content}>
						{/* SUBHEADER */}
						<Heading headingLevel="h3" style="sm" className={styles.MediaStory_subHeading}>
							{subHeading}
						</Heading>

						{/* PARAGRAPH */}
						<Paragraph className={styles.MediaStory_lead}>{lead}</Paragraph>

						{/* PRIMARY BUTTON */}
						<LinkButton style="primary" className={styles.MediaStory_button} url={link.url}>
							{buttonText}
						</LinkButton>
					</div>
				</div>
			</Container>
		</section>
	);
};
